import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input } from '@angular/core';

import { PortalReferralProgramNode } from '@app/services/api/api.types';
import { refereeWindowMonths } from '@app/services/referral-program/referral-detail-timeline.util';
import {
  REFERRAL_ENVELOPE_IMAGE,
  REFERRAL_SHARE_EMAIL_ICON,
  REFERRAL_SHARE_MESSAGES_ICON,
  REFERRAL_SHARE_WHATSAPP_ICON,
} from '@app/utils/referral-assets';
import { buildReferralShareLinks } from '@app/utils/referral-share.util';
import { Tracking } from '@app/services/tracking/tracking.service';
import { PortalTrackingEvents } from '@app/utils/tracking-events';

@Component({
  selector: 'referral-program-hero',
  templateUrl: './referral-program-hero.component.html',
  styleUrls: ['./referral-program-hero.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReferralProgramHeroComponent {
  @Input({ required: true }) program!: PortalReferralProgramNode;

  linkCopied = false;

  REFERRAL_ENVELOPE_IMAGE = REFERRAL_ENVELOPE_IMAGE;
  REFERRAL_SHARE_EMAIL_ICON = REFERRAL_SHARE_EMAIL_ICON;
  REFERRAL_SHARE_MESSAGES_ICON = REFERRAL_SHARE_MESSAGES_ICON;
  REFERRAL_SHARE_WHATSAPP_ICON = REFERRAL_SHARE_WHATSAPP_ICON;

  constructor(private changeDetectorRef: ChangeDetectorRef) {}

  get bookingWindowMonths() {
    return refereeWindowMonths(this.program.refereeWindowDays);
  }

  get shareUrl() {
    return this.program.shareableUrl ?? '';
  }

  get shareLinks() {
    return buildReferralShareLinks(this.shareUrl);
  }

  onLinkCopied() {
    Tracking.track(PortalTrackingEvents.portalReferralLinkCopied);
    this.linkCopied = true;
    this.changeDetectorRef.markForCheck();
    setTimeout(() => {
      this.linkCopied = false;
      this.changeDetectorRef.markForCheck();
    }, 2000);
  }

  shareReferralLink() {
    const url = this.shareUrl;
    if (!url) {
      return;
    }

    if (typeof navigator.share === 'function') {
      void navigator
        .share({
          url,
          title: 'Book with BCC',
          text: url,
        })
        .catch(() => undefined);
      return;
    }

    this.trackShare('sms');
    window.location.href = this.shareLinks.sms;
  }

  trackShare(channel: 'email' | 'sms' | 'whatsapp') {
    const event = {
      email: PortalTrackingEvents.portalReferralShareEmailPressed,
      sms: PortalTrackingEvents.portalReferralShareSmsPressed,
      whatsapp: PortalTrackingEvents.portalReferralShareWhatsappPressed,
    }[channel];

    Tracking.track(event);
  }

  copyReferralLink() {
    const url = this.shareUrl;
    if (!url) {
      return;
    }

    void navigator.clipboard.writeText(url).then(() => this.onLinkCopied());
  }
}
